import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsDashboardActions} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import DashboardLink from 'insights/analytics-dashboards/DashboardLink';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import AnalyticsDescriptionIcon from 'insights/AnalyticsDescriptionIcon';
import {useWritableContext} from 'insights/WritableContext';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {withLoadingScreen} from 'util/Promises';

import {useAnalyticsDashboardContext} from './AnalyticsDashboardDetailsContext';
import {GetAnalyticsDashboard} from './graphql/__generated/GetAnalyticsDashboard';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

export interface EditableAnalyticsDashboardNameProps {
  actions: Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'>;
  analyticsDashboard: AnalyticsDashboard;
}

export default function EditableAnalyticsDashboardName(
  props: EditableAnalyticsDashboardNameProps
): JSX.Element {
  const writable = useWritableContext();
  const {showDashboardLink} = useAnalyticsDashboardContext();
  const [isBeingEdited, setIsBeingEdited] = useState(false);

  function handleChangeName(newName: string): void {
    const dashboardSpecRequest = MakeSpecRequest.onUpdateAnalyticsDashboard({
      ...props.analyticsDashboard,
      name: newName,
    });

    withLoadingScreen(
      props.actions.updateAnalyticsDashboard(props.analyticsDashboard.id, dashboardSpecRequest),
      'Saving dashboard...'
    );
  }
  const hasDescription = props.analyticsDashboard.description !== '';

  // if not writable, the edit button does not show up when hovering the
  // title, so there is no need to hide the info tooltip in that case
  const hideDescriptionIconOnTitleHover = writable && hasDescription;

  return (
    <div className="u-vertical-center-items">
      <span className={css(hideDescriptionIconOnTitleHover && !isBeingEdited && styles.notBeingEdited)}>
        <EditableTextField
          requestChange={handleChangeName}
          value={props.analyticsDashboard.name}
          isDisabled={!writable}
          onIsBeingEditedChanged={setIsBeingEdited}
          initialIsBeingEdited={isBeingEdited}
          dataTestAttrs={{component: 'EditableAnalyticsDashboardName', element: 'editableTextField'}}
        />
      </span>
      {!isBeingEdited && hasDescription && (
        <span className="description-icon-wrapper">
          <span className="u-mini-spacing-left" />
          <AnalyticsDescriptionIcon description={props.analyticsDashboard.description} />
        </span>
      )}
      {!isBeingEdited && showDashboardLink && (
        <DashboardLink
          analyticsDashboard={props.analyticsDashboard}
          className="u-mini-spacing-left"
          showDashboardName={false}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  notBeingEdited: {
    // hide edit button when not hovering the field
    ':not(:hover) .registry-actionGroup': {
      display: 'none',
    },
    // hide the information icon when hovering the field
    ':hover + .description-icon-wrapper': {
      display: 'none',
    },
  },
});
